/**
 * dashboardAnalytics — Admin-Dashboard için saf (yan etkisiz) hesaplamalar.
 *
 * Sadece OKUR: siparişler / kontaktlar dizisi alır, yeni nesneler döner.
 * Ciro kuralı TopKunden ile aynı: yalnızca Bestellung (Angebot değil),
 * Storniert hariç; tutar getOrderTotal (total → gesamtsumme).
 */
import {
  addDays, addMonths, addWeeks, differenceInCalendarDays, endOfDay, format,
  isValid, parseISO, startOfDay, startOfMonth, startOfWeek, startOfYear, subDays, subMonths,
} from 'date-fns';
import { de } from 'date-fns/locale';
import { getOrderItems, getOrderTotal, isBestellung } from '@/lib/orderFields';
import { ORDER_STATUSES } from '@/lib/orderLifecycle';

export const RANGE_PRESETS = [
  { id: '7d', label: '7 Tage' },
  { id: '30d', label: '30 Tage' },
  { id: '90d', label: '90 Tage' },
  { id: 'ytd', label: 'Dieses Jahr', short: 'Jahr' },
  { id: '12m', label: '12 Monate', short: '12 Mon.' },
  { id: 'custom', label: 'Zeitraum' },
];

const DEFAULT_PRESET = '30d';
const DAY_BUCKET_MAX_DAYS = 45;
const WEEK_BUCKET_MAX_DAYS = 180;

/** @param {string|Date|undefined} raw @returns {Date|null} */
function toDate(raw) {
  if (!raw) return null;
  const d = raw instanceof Date ? raw : parseISO(String(raw));
  return isValid(d) ? d : null;
}

/** Sipariş tarihi (created_date → created_at). */
export function orderDate(order) {
  return toDate(order?.created_date || order?.created_at);
}

const customerKey = (o) => o.lead_id || `name:${o.lead_name || 'Unbekannt'}`;
const inRange = (d, from, to) => d !== null && d >= from && d <= to;

/** Ciroya giren siparişler: Bestellung, iptal değil, tarihli. */
function revenueOrders(orders) {
  return orders.filter((o) => isBestellung(o) && o.status !== 'Storniert' && orderDate(o));
}

function ordersIn(orders, from, to) {
  return revenueOrders(orders).filter((o) => inRange(orderDate(o), from, to));
}

/**
 * Zeitraum çözümleyici. Geçersiz özel aralık → 30 Tage.
 * @param {{ preset?: string, from?: string, to?: string }} input
 * @param {Date} [now]
 */
export function resolveRange(input = {}, now = new Date()) {
  const preset = RANGE_PRESETS.some((p) => p.id === input.preset) ? input.preset : DEFAULT_PRESET;
  const to = endOfDay(now);
  let from;
  let end = to;

  if (preset === 'custom') {
    const a = toDate(input.from);
    const b = toDate(input.to);
    if (!a || !b) return resolveRange({ preset: DEFAULT_PRESET }, now);
    const [lo, hi] = a <= b ? [a, b] : [b, a];
    from = startOfDay(lo);
    end = endOfDay(hi);
  } else if (preset === 'ytd') {
    from = startOfYear(now);
  } else if (preset === '12m') {
    from = startOfMonth(subMonths(now, 11));
  } else {
    const days = { '7d': 7, '30d': 30, '90d': 90 }[preset];
    from = startOfDay(subDays(now, days - 1));
  }

  const span = end - from;
  const prevTo = new Date(from.getTime() - 1);
  const prevFrom = new Date(prevTo.getTime() - span);
  return { preset, from, to: end, prevFrom, prevTo, days: differenceInCalendarDays(end, from) + 1 };
}

/** Ciro, sipariş adedi, ortalama sepet, farklı müşteri sayısı. */
export function summarize(orders, from, to) {
  const list = ordersIn(orders, from, to);
  const revenue = list.reduce((s, o) => s + getOrderTotal(o), 0);
  const customers = new Set(list.map(customerKey)).size;
  return { revenue, count: list.length, avgOrder: list.length ? revenue / list.length : 0, customers };
}

/** Önceki döneme göre % değişim; önceki 0 ise null. */
export function percentDelta(current, previous) {
  if (!previous) return null;
  return ((current - previous) / previous) * 100;
}

const BUCKETS = {
  day: { start: startOfDay, next: (d) => addDays(d, 1), label: (d) => format(d, 'dd.MM.', { locale: de }) },
  week: {
    start: (d) => startOfWeek(d, { weekStartsOn: 1 }),
    next: (d) => addWeeks(d, 1),
    label: (d) => `KW ${format(d, 'I', { locale: de })}`,
  },
  month: { start: startOfMonth, next: (d) => addMonths(d, 1), label: (d) => format(d, 'MMM yy', { locale: de }) },
};

/** Zaman serisi; aralık uzunluğuna göre gün / hafta / ay kovaları, boş kovalar 0. */
export function buildSeries(orders, range) {
  const granularity = range.days <= DAY_BUCKET_MAX_DAYS ? 'day' : range.days <= WEEK_BUCKET_MAX_DAYS ? 'week' : 'month';
  const b = BUCKETS[granularity];
  const totals = new Map();
  for (const o of ordersIn(orders, range.from, range.to)) {
    const key = b.start(orderDate(o)).getTime();
    const prev = totals.get(key) || { revenue: 0, orders: 0 };
    totals.set(key, { revenue: prev.revenue + getOrderTotal(o), orders: prev.orders + 1 });
  }
  const points = [];
  for (let d = b.start(range.from); d <= range.to; d = b.next(d)) {
    const t = totals.get(d.getTime()) || { revenue: 0, orders: 0 };
    points.push({ key: d.getTime(), label: b.label(d), ...t });
  }
  return { granularity, points };
}

/** Müşteri bazında ciro sıralaması (+ toplam içindeki pay). */
export function topCustomers(orders, from, to, limit = 5) {
  const map = new Map();
  for (const o of ordersIn(orders, from, to)) {
    const key = customerKey(o);
    const prev = map.get(key) || { key, leadId: o.lead_id || null, name: o.lead_name || 'Unbekannt', orders: 0, revenue: 0 };
    map.set(key, { ...prev, orders: prev.orders + 1, revenue: prev.revenue + getOrderTotal(o) });
  }
  const rows = [...map.values()].sort((a, b) => b.revenue - a.revenue);
  const total = rows.reduce((s, r) => s + r.revenue, 0);
  return rows.slice(0, limit).map((r) => ({ ...r, share: total ? r.revenue / total : 0 }));
}

function lineValue(item) {
  const qty = parseFloat(item.quantity ?? item.menge ?? 0) || 0;
  const price = parseFloat(item.unit_price ?? item.price ?? 0) || 0;
  const explicit = parseFloat(item.total ?? item.netto);
  return { qty, value: Number.isFinite(explicit) && !(qty && price) ? explicit : qty * price };
}

/** Ürün bazında adet + ciro (sipariş kalemlerinden). */
export function topProducts(orders, from, to, limit = 5) {
  const map = new Map();
  for (const o of ordersIn(orders, from, to)) {
    for (const item of getOrderItems(o)) {
      const name = item.product_name || item.name || item.sku || 'Unbenannt';
      const key = item.product_id || item.sku || name;
      const { qty, value } = lineValue(item);
      const prev = map.get(key) || { key, name, quantity: 0, revenue: 0, orders: 0 };
      map.set(key, { ...prev, quantity: prev.quantity + qty, revenue: prev.revenue + value, orders: prev.orders + 1 });
    }
  }
  return [...map.values()].sort((a, b) => b.revenue - a.revenue).slice(0, limit);
}

/**
 * Yeni müşteriler: ilk ciro siparişi aralıkta olanlar.
 * newContacts: aralıkta oluşturulan kontakt kayıtları.
 */
export function newCustomers(orders, leads, from, to) {
  const first = new Map();
  for (const o of revenueOrders(orders)) {
    const key = customerKey(o);
    const d = orderDate(o);
    const prev = first.get(key);
    if (!prev || d < prev.firstDate) {
      first.set(key, { key, leadId: o.lead_id || null, name: o.lead_name || 'Unbekannt', firstDate: d, firstValue: getOrderTotal(o) });
    }
  }
  const list = [...first.values()]
    .filter((c) => inRange(c.firstDate, from, to))
    .sort((a, b) => b.firstDate - a.firstDate);
  const newContacts = leads.filter((l) => inRange(toDate(l.created_date || l.created_at), from, to)).length;
  return { list, newContacts };
}

/** Außendienst-Ranking (agent_email; isim varsa agent_name). */
export function agentRanking(orders, from, to) {
  const map = new Map();
  for (const o of ordersIn(orders, from, to)) {
    const email = (o.agent_email || '').trim();
    if (!email) continue;
    const prev = map.get(email) || { key: email, name: '', orders: 0, revenue: 0 };
    map.set(email, {
      ...prev,
      name: prev.name || o.agent_name || '',
      orders: prev.orders + 1,
      revenue: prev.revenue + getOrderTotal(o),
    });
  }
  return [...map.values()]
    .map((r) => ({ ...r, name: r.name || r.key }))
    .sort((a, b) => b.revenue - a.revenue);
}

/** En az 2 siparişi olan ama `days` gündür sipariş vermeyen müşteriler. */
export function dormantCustomers(orders, now = new Date(), days = 60, limit = 6) {
  const map = new Map();
  for (const o of revenueOrders(orders)) {
    const key = customerKey(o);
    const d = orderDate(o);
    const prev = map.get(key) || { key, leadId: o.lead_id || null, name: o.lead_name || 'Unbekannt', orders: 0, revenue: 0, lastDate: d };
    map.set(key, {
      ...prev,
      orders: prev.orders + 1,
      revenue: prev.revenue + getOrderTotal(o),
      lastDate: d > prev.lastDate ? d : prev.lastDate,
    });
  }
  return [...map.values()]
    .map((c) => ({ ...c, daysSince: differenceInCalendarDays(now, c.lastDate) }))
    .filter((c) => c.orders >= 2 && c.daysSince >= days)
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, limit);
}

/** Aralıktaki Bestellungen'in durum dağılımı (Storniert dahil). */
export function statusBreakdown(orders, from, to) {
  const list = orders.filter((o) => isBestellung(o) && inRange(orderDate(o), from, to));
  return ORDER_STATUSES.map((status) => ({ status, count: list.filter((o) => o.status === status).length }));
}
